// Shared archetype movement helpers (MOB-2/3). PURE (ENG-1).
import { MOB } from '../../config';
import type { MobDef } from '../../registry/schemas';
import { steerToward } from '../pathfind/steer';
import { halfOf, type Entity, type MobContext, type Difficulty } from '../entity';

/** A mob's per-tick ground speed (blocks/tick) from its blocks/s `speed` stat. */
export function tickSpeed(def: MobDef, scale = 1): number {
  return def.speed * MOB.SPEED_TO_TICK * scale;
}

/** Steer toward a horizontal goal at `speed`, setting the entity's move intent. */
export function moveToward(e: Entity, ctx: MobContext, tx: number, tz: number, speed: number): void {
  const r = steerToward(ctx.world, e.x, e.y, e.z, tx, tz, { avoidFluid: e.avoidsWater });
  e.wishX = r.dx * speed;
  e.wishZ = r.dz * speed;
  if (r.jump && e.onGround) e.wishJump = true;
}

/** Move directly away from (fx,fz) — used for fleeing (grazer) and backing off. */
export function moveAway(e: Entity, ctx: MobContext, fx: number, fz: number, speed: number): void {
  moveToward(e, ctx, e.x + (e.x - fx), e.z + (e.z - fz), speed);
}

/** Idle random-walk (MOB-2): occasionally pick a heading and amble, else stand. */
export function wander(e: Entity, ctx: MobContext, def: MobDef): void {
  if (e.wanderTimer > 0) {
    e.wanderTimer--;
  } else {
    e.wanderTimer = MOB.WANDER_INTERVAL + ctx.rng.nextInt(MOB.WANDER_INTERVAL);
    // 65% pick a fresh heading; otherwise stand still for this interval.
    e.wanderAngle = ctx.rng.nextFloat() < 0.65 ? ctx.rng.nextFloat() * Math.PI * 2 : Number.NaN;
  }
  if (Number.isNaN(e.wanderAngle)) return;
  const tx = e.x + Math.cos(e.wanderAngle) * 4;
  const tz = e.z + Math.sin(e.wanderAngle) * 4;
  moveToward(e, ctx, tx, tz, tickSpeed(def, 0.6));
}

/** Chase + strike a target ENTITY (golem/assisting wolf, MOB-2). Steers toward it
 *  and, once within reach, deals `def.attackDamage` on the melee cooldown with an
 *  optional launch velocity (iron-golem toss). No difficulty scaling — a golem
 *  hits mobs at full strength regardless of difficulty. */
export function meleeEntity(e: Entity, def: MobDef, ctx: MobContext, target: Entity, knockV = 0.36): void {
  const reach = halfOf(e, def) + 0.6 + MOB.MELEE.REACH_PAD;
  const flat = Math.hypot(target.x - e.x, target.z - e.z);
  if (flat <= reach && Math.abs(target.y - e.y) < 2.5) {
    e.yaw = Math.atan2(-(target.x - e.x), -(target.z - e.z));
    if (e.attackCooldown === 0 && def.attackDamage) {
      ctx.sys.damage(target, def.attackDamage, ctx.frame, target.x - e.x, target.z - e.z, false, knockV);
      e.attackCooldown = MOB.MELEE.COOLDOWN;
    }
  } else {
    moveToward(e, ctx, target.x, target.z, tickSpeed(def, 1));
  }
}

/** Difficulty multiplier applied to hostile melee/ranged damage (MOB-5). */
export function difficultyScale(d: Difficulty): number {
  switch (d) {
    case 'peaceful': return 0;
    case 'easy': return 0.5;
    case 'hard': return 1.5;
    default: return 1;
  }
}
